import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix para iconos de Leaflet en React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Componente para ajustar la vista del mapa según filtros
const MapUpdater = ({ proyectos, cotizaciones, promesas, filtros }) => {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    let coordenadasParaZoom = [];
    let zoomLevel = 11;
    let centroCalculado = null;

    // CASO 1: Filtro por proyecto específico → Centrar en la COMUNA del proyecto
    if (filtros.proyecto && filtros.proyecto !== '') {
      const proyectoSeleccionado = proyectos.find(p => 
        p.proyecto && p.proyecto.toLowerCase().includes(filtros.proyecto.toLowerCase())
      );
      
      if (proyectoSeleccionado && proyectoSeleccionado.comuna) {
        // Buscar TODOS los puntos de la comuna del proyecto
        const puntosEnLaComuna = [
          ...proyectos.filter(p => p.comuna && p.comuna.toLowerCase() === proyectoSeleccionado.comuna.toLowerCase()),
          ...cotizaciones.filter(c => c.comuna && c.comuna.toLowerCase() === proyectoSeleccionado.comuna.toLowerCase()),
          ...promesas.filter(p => p.comuna && p.comuna.toLowerCase() === proyectoSeleccionado.comuna.toLowerCase())
        ];
        
        coordenadasParaZoom = puntosEnLaComuna
          .map(punto => [punto.latitud, punto.longitud])
          .filter(coord => coord[0] != null && coord[1] != null);
        
        if (coordenadasParaZoom.length > 0) {
          const latSum = coordenadasParaZoom.reduce((sum, coord) => sum + coord[0], 0);
          const lngSum = coordenadasParaZoom.reduce((sum, coord) => sum + coord[1], 0);
          centroCalculado = [latSum / coordenadasParaZoom.length, lngSum / coordenadasParaZoom.length];
        }
        
        zoomLevel = 13;
      }
    }
    
    // CASO 2: Filtro por comuna específica → Centrar en la COMUNA
    else if (filtros.comuna && filtros.comuna !== '') {
      const proyectosDeLaComuna = proyectos.filter(p => 
        p.comuna && p.comuna.toLowerCase().includes(filtros.comuna.toLowerCase())
      );
      
      const cotizacionesDeLaComuna = cotizaciones.filter(c => 
        c.comuna && c.comuna.toLowerCase().includes(filtros.comuna.toLowerCase())
      );

      const promesasDeLaComuna = promesas.filter(p => 
        p.comuna && p.comuna.toLowerCase().includes(filtros.comuna.toLowerCase())
      );
      
      coordenadasParaZoom = [
        ...proyectosDeLaComuna.map(p => [p.latitud, p.longitud]),
        ...cotizacionesDeLaComuna.map(c => [c.latitud, c.longitud]),
        ...promesasDeLaComuna.map(p => [p.latitud, p.longitud])
      ].filter(coord => coord[0] != null && coord[1] != null);
      
      if (coordenadasParaZoom.length > 0) {
        const latSum = coordenadasParaZoom.reduce((sum, coord) => sum + coord[0], 0);
        const lngSum = coordenadasParaZoom.reduce((sum, coord) => sum + coord[1], 0);
        centroCalculado = [latSum / coordenadasParaZoom.length, lngSum / coordenadasParaZoom.length];
      }
      
      zoomLevel = 14;
    }
    
    // CASO 3: Filtro por región específica → Centrar en la REGIÓN
    else if (filtros.region && filtros.region !== '' && !filtros.comuna && !filtros.proyecto) {
      const proyectosDeLaRegion = proyectos.filter(p => 
        p.region && p.region.toLowerCase().includes(filtros.region.toLowerCase())
      );
      
      const cotizacionesDeLaRegion = cotizaciones.filter(c => 
        c.region && c.region.toLowerCase().includes(filtros.region.toLowerCase())
      );

      const promesasDeLaRegion = promesas.filter(p => 
        p.region && p.region.toLowerCase().includes(filtros.region.toLowerCase())
      );
      
      coordenadasParaZoom = [
        ...proyectosDeLaRegion.map(p => [p.latitud, p.longitud]),
        ...cotizacionesDeLaRegion.map(c => [c.latitud, c.longitud]),
        ...promesasDeLaRegion.map(p => [p.latitud, p.longitud])
      ].filter(coord => coord[0] != null && coord[1] != null);
      
      if (coordenadasParaZoom.length > 0) {
        const latSum = coordenadasParaZoom.reduce((sum, coord) => sum + coord[0], 0);
        const lngSum = coordenadasParaZoom.reduce((sum, coord) => sum + coord[1], 0);
        centroCalculado = [latSum / coordenadasParaZoom.length, lngSum / coordenadasParaZoom.length];
      }
      
      zoomLevel = 10;
    }
    
    // CASO 4: Sin filtros específicos - mostrar todos los puntos
    else if (proyectos.length > 0 || cotizaciones.length > 0 || promesas.length > 0) {
      coordenadasParaZoom = [
        ...proyectos.map(p => [p.latitud, p.longitud]),
        ...cotizaciones.map(c => [c.latitud, c.longitud]),
        ...promesas.map(p => [p.latitud, p.longitud])
      ].filter(coord => coord[0] != null && coord[1] != null);
      
      if (coordenadasParaZoom.length > 0) {
        const latSum = coordenadasParaZoom.reduce((sum, coord) => sum + coord[0], 0);
        const lngSum = coordenadasParaZoom.reduce((sum, coord) => sum + coord[1], 0);
        centroCalculado = [latSum / coordenadasParaZoom.length, lngSum / coordenadasParaZoom.length];
      }
      
      zoomLevel = 11;
    }

    // APLICAR EL ZOOM CENTRADO
    if (centroCalculado && coordenadasParaZoom.length > 0) {
      if (filtros.comuna && filtros.comuna !== '') {
        map.setView(centroCalculado, zoomLevel);
        
        setTimeout(() => {
          if (coordenadasParaZoom.length > 10) {
            const bounds = L.latLngBounds(coordenadasParaZoom);
            const boundsCenter = bounds.getCenter();
            
            const distance = map.distance(centroCalculado, [boundsCenter.lat, boundsCenter.lng]);
            if (distance > 5000) {
              map.fitBounds(coordenadasParaZoom, { 
                padding: [20, 20],
                maxZoom: Math.min(zoomLevel, 15)
              });
            }
          }
        }, 200);
      } else {
        map.setView(centroCalculado, zoomLevel);
        
        setTimeout(() => {
          if (coordenadasParaZoom.length > 1) {
            const bounds = map.getBounds();
            const puntosVisibles = coordenadasParaZoom.every(coord => bounds.contains(coord));
            
            if (!puntosVisibles) {
              map.fitBounds(coordenadasParaZoom, { 
                padding: [30, 30],
                maxZoom: zoomLevel
              });
            }
          }
        }, 100);
      }
    } else {
      map.setView([-33.4489, -70.6693], 11);
    }
  }, [map, proyectos, cotizaciones, promesas, filtros]);

  return null;
};

// Crear icono de estrella personalizado con color específico usando SVG
const createStarIcon = (color) => {
  return L.divIcon({
    html: `
      <div style="
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        filter: drop-shadow(2px 2px 4px rgba(0,0,0,0.6));
        pointer-events: none;
      ">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="${color}" stroke="#000000" stroke-width="1.5" style="pointer-events: none;">
          <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
        </svg>
      </div>
    `,
    className: 'custom-star-icon',
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16]
  });
};

// Crear icono circular personalizado para cotizaciones
const createCircleIcon = (color) => {
  return L.divIcon({
    html: `
      <div style="
        width: 16px;
        height: 16px;
        background-color: ${color};
        border: 2px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      "></div>
    `,
    className: 'custom-circle-icon',
    iconSize: [16, 16],
    iconAnchor: [8, 8],
    popupAnchor: [0, -8]
  });
};

// Crear icono circular personalizado para promesas
const createPromiseIcon = (color) => {
  return L.divIcon({
    html: `
      <div style="
        width: 18px;
        height: 18px;
        background-color: ${color};
        border: 2px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      "></div>
    `,
    className: 'custom-promise-icon',
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    popupAnchor: [0, -9]
  });
};

const MapaLeaflet = ({ 
  proyectos = [], 
  cotizaciones = [], 
  promesas = [], 
  filtros = {},
  // Datos originales para estadísticas
  cotizacionesOriginales = [],
  promesasOriginales = []
}) => {
  const mapRef = useRef(null);
  const [leyendaExpandida, setLeyendaExpandida] = useState(false);

  // Usar datos originales para estadísticas si están disponibles
  const cotizacionesParaEstadisticas = cotizacionesOriginales.length > 0 ? cotizacionesOriginales : cotizaciones;
  const promesasParaEstadisticas = promesasOriginales.length > 0 ? promesasOriginales : promesas;

  // Calcular centro por defecto
  const defaultCenter = [-33.4489, -70.6693]; // Santiago, Chile
  const defaultZoom = 11;

  return (
    <div className="w-full h-full relative">
      <MapContainer
        center={defaultCenter}
        zoom={defaultZoom}
        style={{ height: '100%', width: '100%' }}
        ref={mapRef}
      >
        {/* Capa de mapa base */}
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />

        {/* Componente para actualizar vista */}
        <MapUpdater 
          proyectos={proyectos} 
          cotizaciones={cotizaciones} 
          promesas={promesas} 
          filtros={filtros} 
        />

        {/* Marcadores de proyectos (estrellas) */}
        {proyectos.map((proyecto) => {
          if (!proyecto.latitud || !proyecto.longitud) return null;
          
          // Determinar si el proyecto está dentro de los filtros aplicados
          const proyectoEstaEnFiltros = () => {
            if (!filtros.region && !filtros.comuna && !filtros.proyecto) {
              return true;
            }
            
            let proyectoEnFiltros = true;
            
            if (filtros.region && proyecto.region) {
              proyectoEnFiltros = proyectoEnFiltros && 
                proyecto.region.toLowerCase().includes(filtros.region.toLowerCase());
            }
            
            if (filtros.comuna && proyecto.comuna) {
              proyectoEnFiltros = proyectoEnFiltros && 
                proyecto.comuna.toLowerCase().includes(filtros.comuna.toLowerCase());
            }
            
            if (filtros.proyecto && proyecto.proyecto) {
              proyectoEnFiltros = proyectoEnFiltros && 
                proyecto.proyecto.toLowerCase().includes(filtros.proyecto.toLowerCase());
            }
            
            return proyectoEnFiltros;
          };
          
          // Determinar el color del proyecto
          let colorProyecto;
          if (proyectoEstaEnFiltros()) {
            colorProyecto = proyecto.color || '#FFD700';
          } else {
            colorProyecto = '#FFD700';
          }
          
          return (
            <Marker
              key={`proyecto-${proyecto.id}`}
              position={[proyecto.latitud, proyecto.longitud]}
              icon={createStarIcon(colorProyecto)}
            >
              <Popup>
                <div style={{ minWidth: '250px' }}>
                  <h3 style={{ 
                    color: colorProyecto, 
                    textAlign: 'center',
                    margin: '0 0 10px 0',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill={colorProyecto} stroke="rgba(0,0,0,0.3)" strokeWidth="0.5">
                      <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                    </svg>
                    {proyecto.proyecto}
                  </h3>
                  <hr style={{ margin: '10px 0', borderColor: colorProyecto }} />
                  <p><strong>Tipo:</strong> Ubicación del Proyecto</p>
                  <p><strong>Región:</strong> {proyecto.region || 'No especificada'}</p>
                  <p><strong>Comuna:</strong> {proyecto.comuna || 'No especificada'}</p>
                  {proyecto.direccion && (
                    <p><strong>Dirección:</strong> {proyecto.direccion}</p>
                  )}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    marginTop: '10px',
                    padding: '8px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    border: `2px solid ${colorProyecto}`
                  }}>
                    <strong style={{ marginRight: '8px' }}>Color del proyecto:</strong>
                    <div style={{
                      width: '24px',
                      height: '24px',
                      backgroundColor: colorProyecto,
                      border: '2px solid white',
                      borderRadius: '4px',
                      marginRight: '8px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}></div>
                    <span style={{
                      backgroundColor: colorProyecto,
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                    }}>
                      {colorProyecto}
                    </span>
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}

        {/* Marcadores de cotizaciones (círculos) */}
        {cotizaciones.map((cotizacion) => {
          if (!cotizacion.latitud || !cotizacion.longitud) return null;
          
          // Buscar información del proyecto relacionado
          const proyectoRelacionado = proyectos.find(p => p.proyecto === cotizacion.proyecto);
          
          // Determinar si la cotización está dentro de los filtros aplicados
          const estaEnFiltros = () => {
            if (!filtros.comuna || filtros.comuna === '') {
              return true; // Si no hay filtro de comuna, todas están "en filtros"
            }
            
            if (!proyectoRelacionado) return false;
            
            // Verificar si el PROYECTO está en la comuna filtrada
            return proyectoRelacionado.comuna?.toLowerCase().includes(filtros.comuna.toLowerCase());
          };
          
          // Determinar el color según si el PROYECTO está en filtros o no
          let color;
          if (estaEnFiltros()) {
            // El PROYECTO está en filtros → usar color del proyecto
            color = proyectoRelacionado?.color || '#3B82F6';
          } else {
            // El PROYECTO NO está en filtros (cotización de otras comunas) → AZUL MARINO
            color = '#1e3a8a';
          }
          
          return (
            <Marker
              key={`cotizacion-${cotizacion.id}`}
              position={[cotizacion.latitud, cotizacion.longitud]}
              icon={createCircleIcon(color)}
            >
              <Popup>
                <div style={{ minWidth: '250px' }}>
                  <h4 style={{ 
                    color: color, 
                    textAlign: 'center',
                    margin: '0 0 10px 0'
                  }}>
                    🔵 Cotización #{cotizacion.id}
                  </h4>
                  <hr style={{ margin: '10px 0' }} />
                  <p><strong>Tipo:</strong> Punto de Cotización</p>
                  <p><strong>Proyecto:</strong> {cotizacion.proyecto || 'Sin asignar'}</p>
                  {proyectoRelacionado?.comuna && (
                    <p><strong>Comuna del proyecto:</strong> 
                      <span style={{
                        backgroundColor: '#e3f2fd',
                        color: '#1565c0',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        marginLeft: '5px',
                        fontSize: '12px'
                      }}>
                        {proyectoRelacionado.comuna}
                      </span>
                    </p>
                  )}
                  {cotizacion.region && (
                    <p><strong>Región:</strong> {cotizacion.region}</p>
                  )}
                  {cotizacion.comuna && (
                    <p><strong>Comuna del cliente:</strong> {cotizacion.comuna}</p>
                  )}
                  {cotizacion.direccion && (
                    <p><strong>Dirección:</strong> {cotizacion.direccion}</p>
                  )}
                  {cotizacion.cliente && (
                    <p><strong>Cliente:</strong> {cotizacion.cliente}</p>
                  )}
                  <p><strong>Estado geográfico:</strong>
                    <span style={{
                      backgroundColor: estaEnFiltros() ? '#dcfce7' : '#fef3c7',
                      color: estaEnFiltros() ? '#16a34a' : '#d97706',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      marginLeft: '5px',
                      fontSize: '12px'
                    }}>
                      {estaEnFiltros() ? 'Proyecto en filtros' : 'Cliente en filtros (otras comunas)'}
                    </span>
                  </p>
                </div>
              </Popup>
            </Marker>
          );
        })}

        {/* Marcadores de promesas */}
        {promesas.map((promesa) => {
          if (!promesa.latitud || !promesa.longitud) return null;
          
          // Buscar información del proyecto relacionado
          const proyectoRelacionado = proyectos.find(p => p.proyecto === promesa.proyecto);
          
          // Determinar si la promesa está dentro de los filtros aplicados
          const estaEnFiltros = () => {
            if (!filtros.comuna || filtros.comuna === '') {
              return true; // Si no hay filtro de comuna, todas están "en filtros"
            }
            
            if (!proyectoRelacionado) return false;
            
            // Verificar si el PROYECTO está en la comuna filtrada
            return proyectoRelacionado.comuna?.toLowerCase().includes(filtros.comuna.toLowerCase());
          };
          
          // Determinar el color según si el PROYECTO está en filtros o no
          let color;
          if (estaEnFiltros()) {
            // El PROYECTO está en filtros → usar color del proyecto
            color = proyectoRelacionado?.color || '#FFA500';
          } else {
            // El PROYECTO NO está en filtros (promesa de otras comunas) → NARANJA
            color = '#FFA500';
          }
          
          return (
            <Marker
              key={`promesa-${promesa.id}`}
              position={[promesa.latitud, promesa.longitud]}
              icon={createPromiseIcon(color)}
            >
              <Popup>
                <div style={{ minWidth: '250px' }}>
                  <h4 style={{ 
                    color: color, 
                    textAlign: 'center',
                    margin: '0 0 10px 0'
                  }}>
                    🟡 Promesa #{promesa.id}
                  </h4>
                  <hr style={{ margin: '10px 0' }} />
                  <p><strong>Tipo:</strong> Punto de Promesa</p>
                  <p><strong>Proyecto:</strong> {promesa.proyecto || 'Sin asignar'}</p>
                  {proyectoRelacionado?.comuna && (
                    <p><strong>Comuna del proyecto:</strong> 
                      <span style={{
                        backgroundColor: '#e3f2fd',
                        color: '#1565c0',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        marginLeft: '5px',
                        fontSize: '12px'
                      }}>
                        {proyectoRelacionado.comuna}
                      </span>
                    </p>
                  )}
                  {promesa.region && (
                    <p><strong>Región:</strong> {promesa.region}</p>
                  )}
                  {promesa.comuna && (
                    <p><strong>Comuna del cliente:</strong> {promesa.comuna}</p>
                  )}
                  {promesa.direccion && (
                    <p><strong>Dirección:</strong> {promesa.direccion}</p>
                  )}
                  {promesa.cliente && (
                    <p><strong>Cliente:</strong> {promesa.cliente}</p>
                  )}
                  <p><strong>Estado geográfico:</strong>
                    <span style={{
                      backgroundColor: estaEnFiltros() ? '#dcfce7' : '#fef3c7',
                      color: estaEnFiltros() ? '#16a34a' : '#d97706',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      marginLeft: '5px',
                      fontSize: '12px'
                    }}>
                      {estaEnFiltros() ? 'Proyecto en filtros' : 'Cliente en filtros (otras comunas)'}
                    </span>
                  </p>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* Leyenda flotante CON ESTADÍSTICAS COMPLETAS */}
      {(proyectos.length > 0 || cotizaciones.length > 0 || promesas.length > 0) && (
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          padding: '15px',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          zIndex: 1000,
          fontSize: '14px',
          minWidth: '250px',
          maxHeight: '500px',
          overflowY: 'auto'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <h4 style={{ margin: '0', color: '#333', fontSize: '16px' }}>🗺️ Leyenda</h4>
            <button
              onClick={() => setLeyendaExpandida(!leyendaExpandida)}
              style={{
                padding: '4px 8px',
                fontSize: '10px',
                backgroundColor: '#f3f4f6',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#e5e7eb'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#f3f4f6'}
            >
              {leyendaExpandida ? "📋→📊" : "📊→📋"}
            </button>
          </div>
          
          {/* Totales principales */}
          <div style={{ marginBottom: '15px' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#FFD700" stroke="rgba(0,0,0,0.3)" strokeWidth="0.5" style={{ marginRight: '8px' }}>
                <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
              </svg>
              <span style={{ fontSize: '14px', fontWeight: '500' }}>
                Ubicaciones de Proyectos ({proyectos.length})
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
              <div style={{ 
                width: '16px', 
                height: '16px', 
                borderRadius: '50%', 
                backgroundColor: '#3B82F6',
                marginRight: '8px',
                border: '2px solid white',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
              }}></div>
              <span style={{ fontSize: '14px', fontWeight: '500' }}>
                Cotizaciones ({cotizacionesParaEstadisticas.length})
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
              <div style={{ 
                width: '18px', 
                height: '18px', 
                borderRadius: '50%', 
                backgroundColor: '#FFA500',
                marginRight: '8px',
                border: '2px solid white',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
              }}></div>
              <span style={{ fontSize: '14px', fontWeight: '500' }}>
                Promesas ({promesasParaEstadisticas.length})
              </span>
            </div>
          </div>

          {/* Lista de proyectos con estadísticas */}
          {proyectos.length > 0 && (
            <div>
              <h5 style={{ 
                margin: '0 0 10px 0', 
                fontSize: '12px', 
                color: '#666',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                borderBottom: '1px solid #e5e7eb',
                paddingBottom: '5px'
              }}>
                PROYECTOS POR COLOR:
              </h5>
              
              {(() => {
                // Obtener proyectos únicos y calcular estadísticas
                const proyectosUnicos = [...new Set(proyectos.map(p => p.proyecto))];
                const proyectosConEstadisticas = proyectosUnicos.map(nombreProyecto => {
                  const proyecto = proyectos.find(p => p.proyecto === nombreProyecto);
                  const cantidadCotizaciones = cotizacionesParaEstadisticas.filter(c => c.proyecto === nombreProyecto).length;
                  const cantidadPromesas = promesasParaEstadisticas.filter(p => p.proyecto === nombreProyecto).length;
                  
                  return {
                    nombre: nombreProyecto,
                    proyecto: proyecto,
                    cotizaciones: cantidadCotizaciones,
                    promesas: cantidadPromesas
                  };
                });

                // Mostrar según estado expandido
                const proyectosAMostrar = leyendaExpandida ? 
                  proyectosConEstadisticas : 
                  proyectosConEstadisticas.slice(0, 6);

                return (
                  <div>
                    {proyectosAMostrar.map((item, index) => (
                      <div key={index} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        marginBottom: '6px',
                        fontSize: '11px',
                        padding: '4px 6px',
                        backgroundColor: 'rgba(249, 250, 251, 0.8)',
                        borderRadius: '4px',
                        border: '1px solid #f3f4f6'
                      }}>
                        <div style={{
                          width: '14px',
                          height: '14px',
                          backgroundColor: item.proyecto?.color || '#FFD700',
                          marginRight: '8px',
                          borderRadius: '2px',
                          border: '1px solid #d1d5db',
                          flexShrink: 0
                        }}></div>
                        <span style={{ 
                          flex: 1, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap',
                          fontWeight: '500',
                          color: '#374151'
                        }}>
                          {leyendaExpandida ? item.nombre : 
                           (item.nombre.length > 18 ? `${item.nombre.substring(0, 18)}...` : item.nombre)}
                        </span>
                        <div style={{ display: 'flex', gap: '4px', marginLeft: '8px', flexShrink: 0 }}>
                          {item.cotizaciones > 0 && (
                            <span style={{ 
                              backgroundColor: '#dbeafe', 
                              color: '#1e40af',
                              padding: '2px 4px', 
                              borderRadius: '3px',
                              fontSize: '9px',
                              fontWeight: 'bold'
                            }}>
                              C{item.cotizaciones}
                            </span>
                          )}
                          {item.promesas > 0 && (
                            <span style={{ 
                              backgroundColor: '#fef3c7', 
                              color: '#92400e',
                              padding: '2px 4px', 
                              borderRadius: '3px',
                              fontSize: '9px',
                              fontWeight: 'bold'
                            }}>
                              P{item.promesas}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                    
                    {!leyendaExpandida && proyectosConEstadisticas.length > 6 && (
                      <div style={{ 
                        fontSize: '10px', 
                        color: '#6b7280', 
                        fontStyle: 'italic', 
                        textAlign: 'center',
                        marginTop: '8px',
                        padding: '4px',
                        backgroundColor: '#f9fafb',
                        borderRadius: '3px'
                      }}>
                        ... y {proyectosConEstadisticas.length - 6} más
                      </div>
                    )}
                  </div>
                );
              })()}
              
              <div style={{ 
                marginTop: '12px', 
                fontSize: '10px', 
                color: '#6b7280',
                fontStyle: 'italic',
                textAlign: 'center',
                borderTop: '1px solid #e5e7eb',
                paddingTop: '8px'
              }}>
                <strong>C:</strong> Cotizaciones, <strong>P:</strong> Promesas
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MapaLeaflet;